import { Link } from 'react-router-dom';
import { moviesData } from '@/mocks/movies';
import {
  getFeatured,
  getHot,
  getLatest,
  getByType,
  typeLabel,
  formatViews,
} from '@/utils/movie';
import { useSeo } from '@/hooks/useSeo';
import MovieCard from '@/components/base/MovieCard';
import SectionHeader from '@/components/base/SectionHeader';
import HeroCarousel from '@/pages/home/components/HeroCarousel';
import CategoryEntry from '@/pages/home/components/CategoryEntry';

const categories = [
  { type: 'tv', desc: '涵盖最新热播剧集与口碑佳作', accent: 'primary' as const },
  { type: 'movie', desc: '精选高口碑电影与院线佳作', accent: 'accent' as const },
  { type: 'variety', desc: '欢乐下饭的综艺节目合集', accent: 'secondary' as const },
];

export default function Home() {
  useSeo({
    title: '牛牛视频—热门影视视频平台，全网电视剧电影聚合推荐',
    description:
      '牛牛视频为您提供最新最全的影视视频内容，包括热门电视剧、电影、综艺等多种类型，满足不同用户的观影需求，轻松浏览，随时在线观看最新影视资源。',
    keywords: '牛牛视频,影视视频,电视剧,电影,综艺,在线观看,影视平台',
  });

  const featured = getFeatured(moviesData);
  const latest = getLatest(moviesData, 12);
  const hot = getHot(moviesData, 10);

  return (
    <>
      <HeroCarousel items={featured} />

      {/* 分类快捷入口 */}
      <section className="w-full px-4 md:px-8 pt-8 md:pt-10">
        <CategoryEntry />
      </section>

      {/* 最新影视推荐 */}
      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <SectionHeader
          title="最新影视推荐"
          subtitle="按更新时间倒序，第一时间发现新上线的热门影视"
          moreLink="/category/tv"
          moreText="更多内容"
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-5">
          {latest.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>

      {/* 热门影视榜单 */}
      <section className="w-full bg-background-100 border-y border-background-200/70">
        <div className="w-full px-4 md:px-8 py-12 md:py-16">
          <SectionHeader
            title="热门影视榜单"
            subtitle="依据播放热度与观众关注度综合排序"
            accent="accent"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            {hot.map((movie, i) => (
              <Link
                key={movie.id}
                to={`/detail/${movie.id}`}
                className="group flex items-center gap-4 p-3 rounded-lg hover:bg-background-50 transition-colors cursor-pointer"
              >
                <span
                  className={`w-8 text-center text-lg md:text-xl font-black shrink-0 ${
                    i < 3 ? 'text-accent-500' : 'text-foreground-400'
                  }`}
                >
                  {i + 1}
                </span>
                <span className="w-14 h-20 shrink-0 rounded-md overflow-hidden bg-background-200">
                  <img
                    src={movie.cover}
                    alt={movie.title}
                    title={`${movie.title} - 牛牛视频`}
                    className="w-full h-full object-cover object-top"
                  />
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm md:text-base font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
                    {movie.title}
                  </h3>
                  <p className="mt-1 text-xs text-foreground-500 line-clamp-1">
                    {movie.cast.slice(0, 2).join(' / ')}
                  </p>
                  <div className="mt-1.5 flex items-center gap-3 text-xs text-foreground-400">
                    <span className="px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-800 font-medium">
                      {typeLabel(movie.type)}
                    </span>
                    <span>{movie.year}</span>
                    <span>{formatViews(movie.views)}播放</span>
                  </div>
                </div>
                <span className="hidden sm:flex items-center gap-1 text-sm font-bold text-accent-600 shrink-0">
                  <i className="ri-fire-fill" />
                  {movie.rating.toFixed(1)}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 分类精选 */}
      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <SectionHeader title="分类精选" subtitle="按频道浏览，快速找到你想看的内容" accent="secondary" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6">
          {categories.map((cat) => {
            const items = getByType(moviesData, cat.type).slice(0, 5);
            return (
              <div
                key={cat.type}
                className="rounded-lg bg-background-100 border border-background-200/70 p-5 md:p-6 flex flex-col"
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-base md:text-lg font-bold text-foreground-950">
                      {typeLabel(cat.type)}频道
                    </h3>
                    <p className="mt-1 text-xs text-foreground-500">{cat.desc}</p>
                  </div>
                  <Link
                    to={`/category/${cat.type}`}
                    className="shrink-0 flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
                  >
                    全部
                    <i className="ri-arrow-right-s-line" />
                  </Link>
                </div>
                <ul className="space-y-3 flex-1">
                  {items.map((movie, i) => (
                    <li key={movie.id}>
                      <Link
                        to={`/detail/${movie.id}`}
                        className="group flex items-center gap-3 cursor-pointer"
                      >
                        <span className="text-xs font-bold text-foreground-300 w-4 shrink-0">
                          {i + 1}
                        </span>
                        <span className="w-12 h-[68px] shrink-0 rounded-md overflow-hidden bg-background-200">
                          <img
                            src={movie.cover}
                            alt={movie.title}
                            title={`${movie.title} - 牛牛视频`}
                            className="w-full h-full object-cover object-top"
                          />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
                            {movie.title}
                          </span>
                          <span className="block mt-0.5 text-xs text-foreground-400 line-clamp-1">
                            {movie.category} · {movie.status}
                          </span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* 平台简介 */}
      <section className="w-full px-4 md:px-8 pb-16 md:pb-20">
        <div className="rounded-lg bg-primary-500 px-6 md:px-12 py-10 md:py-14 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-background-50/15 text-background-50 text-xs font-medium">
            <i className="ri-video-line" />
            影视内容聚合平台
          </span>
          <h2 className="mt-4 text-2xl md:text-4xl font-black text-background-50">
            发现值得一看的好影视
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-sm md:text-base text-primary-100 leading-relaxed">
            牛牛视频聚合全网热门电视剧、电影与综艺内容，提供清晰的分类导航与丰富的影视资讯，帮助你轻松浏览、快速发现最新最热的影视资源。
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/category/tv"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 hover:bg-accent-600 text-accent-950 text-sm font-bold whitespace-nowrap transition-colors cursor-pointer"
            >
              <i className="ri-tv-2-line" />
              开始浏览
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-50/10 hover:bg-background-50/20 border border-background-50/40 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
            >
              了解我们
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}